import { useSyncExternalStore } from "react";
import { miQsWeatherDomains } from "@/constants/domain.config";

export type MqsInterstitialStatus =
  | "idle"
  | "loading"
  | "ready"
  | "showing"
  | "unsupported"
  | "empty";

interface MqsInterstitialGateSnapshot {
  status: MqsInterstitialStatus;
  activeRequestSeq: number;
  suppressDisplayRequests: boolean;
}

type Listener = () => void;

const ACTIVE_INTERSTITIAL_STATUSES: MqsInterstitialStatus[] = ["showing"];

const listeners = new Set<Listener>();

let snapshot: MqsInterstitialGateSnapshot = {
  status: "idle",
  activeRequestSeq: 0,
  suppressDisplayRequests: false,
};

const emitChange = () => {
  listeners.forEach(listener => listener());
};

const buildSnapshot = (status: MqsInterstitialStatus, activeRequestSeq: number): MqsInterstitialGateSnapshot => ({
  status,
  activeRequestSeq,
  // 只在第二轮及以后 interstitial 实际展示时暂停普通 GPT 请求；
  // 已缓存但未展示的 ready 状态不能拦截新页面广告，否则会留下空占位。
  suppressDisplayRequests: activeRequestSeq > 1 && ACTIVE_INTERSTITIAL_STATUSES.includes(status),
});

export const updateMqsInterstitialGate = (status: MqsInterstitialStatus, activeRequestSeq: number) => {
  const nextSnapshot = buildSnapshot(status, activeRequestSeq);
  if (
    snapshot.status === nextSnapshot.status &&
    snapshot.activeRequestSeq === nextSnapshot.activeRequestSeq &&
    snapshot.suppressDisplayRequests === nextSnapshot.suppressDisplayRequests
  ) {
    return;
  }

  snapshot = nextSnapshot;
  emitChange();
};

export const resetMqsInterstitialGate = () => {
  updateMqsInterstitialGate("idle", 0);
};

export const getMqsInterstitialGateSnapshot = () => snapshot;

export const subscribeMqsInterstitialGate = (listener: Listener) => {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
};

export const useMqsInterstitialGateSnapshot = () =>
  useSyncExternalStore(subscribeMqsInterstitialGate, getMqsInterstitialGateSnapshot, getMqsInterstitialGateSnapshot);

export const shouldPauseMqsGoogleAdRequest = (hostName: string, suppressDisplayRequests: boolean) =>
  miQsWeatherDomains.includes(hostName) && suppressDisplayRequests;
